import { Box, Fab, ListItemIcon, ListItemText, Menu, ListItem, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useEffect, useState } from 'react';
import { checkNavigationForUserPermission } from 'components/navigation/navigationUtils';
import { useDispatch, useSelector } from 'react-redux';
import {
	accountDataSelector,
	accountExpiringDialogOpenSelector,
	featuresSelector,
	setAccountExpiringDialogOpen,
	userRoleSelector
} from 'reducers/userAuthSlice';
import { logEvent } from 'services/firebaseLogger';
import { handleRoute } from 'utils/helpers';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getActiveMainRoute } from 'utils/urlHelpers';
import { SEARCH_FILTERS } from 'constants/localStorageConstants';
import dayjs from 'utils/customDayJS';
import { linkBaseUrl } from 'constants/generalConstants';
import ErrorBoundary from 'components/elements/ErrorBoundary';
import { $bluishGray60, $primary } from 'constants/styles';
import UserMenuContent from 'components/navigation/UserMenuContent';
import AccountExpiringDialog from 'components/authentication/AccountExpiringDialog';
import { useTranslation } from 'react-i18next';
import { getAircraftMakes, getAircraftModels } from 'apis/aircraft.api';
import { getBatteryMakes, getBatteryModels } from 'apis/batteries.api';
import {
	aircraftMakesSelector,
	aircraftModelsSelector,
	batteryMakesSelector,
	batteryModelsSelector
} from 'reducers/globalSlice';
const MobileAccountContainer = () => {
	const styles = {
		fullWidth: { width: '100%', height: '100%' },
		main: {
			height: '100vh',
			width: '100vw'
		},
		listItem: {
			paddingLeft: '5px'
		},
		activeIcon: {
			display: 'flex',
			justifyContent: 'center',
			cursor: 'pointer',
			'& svg > path': {
				stroke: $primary
			},
			'& svg > circle': {
				stroke: $primary
			},
			'& svg > rect': {
				fill: $primary,
				stroke: $primary
			}
		},
		inactiveIcon: {
			display: 'flex',
			justifyContent: 'center',
			cursor: 'pointer',
			'& svg > path': {
				stroke: $bluishGray60
			},
			'& svg > circle': {
				stroke: $bluishGray60
			},
			'& svg > rect': {
				fill: $bluishGray60,
				stroke: $bluishGray60
			}
		}
	};
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { pathname } = location;
	const features = useSelector(featuresSelector);
	const activeMainRoute = getActiveMainRoute(pathname);

	const [menuAnchorElement, setMenuAnchorElement] = useState(null);
	const menuOpen = Boolean(menuAnchorElement);
	const userRole = useSelector(userRoleSelector);
	const userPermittedNavigationItems = checkNavigationForUserPermission({
		userRole,
		mobile: true
	});

	const localStorageSearchFilters = localStorage.getItem(SEARCH_FILTERS);
	const startOfYear = dayjs().startOf('year').unix(); //for use in reportsModulesList
	const currentDay = dayjs().unix(); //for use in reportsModulesList

	const accountExpiringDialogOpen = useSelector(accountExpiringDialogOpenSelector);
	const accountData = useSelector(accountDataSelector);
	const batteryMakes = useSelector(batteryMakesSelector);
	const batteryModels = useSelector(batteryModelsSelector);
	const aircraftMakes = useSelector(aircraftMakesSelector);
	const aircraftModels = useSelector(aircraftModelsSelector);

	const handleClose = () => {
		dispatch(setAccountExpiringDialogOpen(false));
	};

	const handleOpenMenu = e => {
		setMenuAnchorElement(e.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchorElement(null);
	};

	const handleClickMenuItem = ({ value, path }) => {
		let missionsParameters = 'appends[]=tags&order_by=starts_at&order=desc&search=';
		let reportsDashboardParameters = `start=${startOfYear}&stop=${currentDay}`;

		if (
			localStorageSearchFilters &&
			Object.hasOwnProperty.call(JSON.parse(localStorageSearchFilters), 'missions')
		) {
			missionsParameters = JSON.parse(localStorageSearchFilters).missions;
		}

		if (
			localStorageSearchFilters &&
			Object.hasOwnProperty.call(JSON.parse(localStorageSearchFilters), 'reportsDashboard')
		) {
			reportsDashboardParameters = JSON.parse(localStorageSearchFilters).reportsDashboard;
		}

		// Change route only if it is different
		// Use localStorage param values for manage/missions and reports/dashboard
		if (activeMainRoute !== value) {
			logEvent(`menu_click_${value}`);
			const link =
				value === 'manage' && missionsParameters
					? `manage/missions?${missionsParameters}`
					: value === 'reports' && reportsDashboardParameters
						? `reports/dashboard?${reportsDashboardParameters}`
						: path;
			handleRoute(link, navigate);
			handleCloseMenu();
		}
	};

	const handleClickEnterpriseItem = () => {
		window.open(`${linkBaseUrl}/contact/`);
	};

	useEffect(() => {
		const fetchAircraftMakes = async () => {
			await dispatch(getAircraftMakes());
		};
		if (aircraftMakes.length === 0) {
			fetchAircraftMakes();
		}
	}, [aircraftMakes, dispatch]);

	useEffect(() => {
		const fetchAircraftModels = async () => {
			await dispatch(getAircraftModels());
		};
		if (aircraftModels.length === 0) {
			fetchAircraftModels();
		}
	}, [aircraftModels, dispatch]);

	useEffect(() => {
		const fetchBatteryMakes = async () => {
			await dispatch(getBatteryMakes());
		};
		if (batteryMakes.length === 0) {
			fetchBatteryMakes();
		}
	}, [batteryMakes, dispatch]);

	useEffect(() => {
		const fetchBatteryModels = async () => {
			await dispatch(getBatteryModels());
		};
		if (batteryModels.length === 0) {
			fetchBatteryModels();
		}
	}, [batteryModels, dispatch]);

	return (
		<Box sx={styles.main}>
			{accountData && !accountExpiringDialogOpen && (
				<>
					<Fab
						color='primary'
						onClick={handleOpenMenu}
						sx={{ position: 'fixed', bottom: 12, left: 10, zIndex: 1000 }}
					>
						<MenuIcon />
					</Fab>
					<Menu anchorEl={menuAnchorElement} open={menuOpen} onClose={handleCloseMenu}>
						{userPermittedNavigationItems.map((item, key) => {
							return item?.value && // if item has value key.
								features && // if features object exists.
								Object.prototype.hasOwnProperty.call(features, item.featureValue) && // check if value key exists in features object.
								!features[item.featureValue] ? (
								<ListItem button key={key} onClick={handleClickEnterpriseItem} sx={styles.listItem}>
									<ListItemIcon>{item.icon}</ListItemIcon>
									<ListItemText>
										{item.name} {t('Enterprise Only')}
									</ListItemText>
								</ListItem>
							) : (
								<ListItem
									button
									key={key}
									onClick={() => handleClickMenuItem(item)}
									sx={styles.listItem}
								>
									<ListItemIcon
										sx={activeMainRoute === item.value ? styles.activeIcon : styles.inactiveIcon}
									>
										{item.icon}
									</ListItemIcon>
									<ListItemText>{item.name}</ListItemText>
								</ListItem>
							);
						})}
						<Divider />
						<UserMenuContent handleClose={handleCloseMenu} />
					</Menu>
					<ErrorBoundary>
						<Box component='main' sx={styles.fullWidth}>
							<Outlet />
						</Box>
					</ErrorBoundary>
				</>
			)}
			<AccountExpiringDialog open={accountExpiringDialogOpen} handleClose={handleClose} />
		</Box>
	);
};

export default MobileAccountContainer;
